import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AUTH_TYPE_LABELS, type AuthTypeName } from "../../../../../../shared/index";

/**
 * Create a client from a preset or an existing client, rather than from a blank form.
 *
 * A blank new client is a client that can call nothing — the state the legacy tool left
 * behind by default and never surfaced anywhere.
 */
export default function B2bNewClient() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [authType, setAuthType] = useState<AuthTypeName>("singleFeature");
  const [source, setSource] = useState("");
  const [created, setCreated] = useState<{ key: string; token: string | null; id: number } | null>(
    null
  );

  const clients = useQuery({
    queryKey: ["b2b", "clients", { pageSize: 500 }],
    queryFn: () => api.b2b.listClients({ pageSize: 500 }),
  });
  const settings = useQuery({ queryKey: ["b2b", "settings"], queryFn: api.b2b.settings });

  const create = useMutation({
    mutationFn: () =>
      api.b2b.createClient({
        name,
        authType,
        copyFromClientId: source.startsWith("client:") ? Number(source.slice(7)) : undefined,
        presetId: source.startsWith("preset:") ? source.slice(7) : undefined,
      }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["b2b"] });
      setCreated({ key: data.clientKey, token: data.token, id: data.clientId });
      if (!data.applied) {
        toast.message("Dry run — nothing was created", {
          description: "The change was recorded and emailed, but no client exists yet.",
        });
      }
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : "Could not create the client"),
  });

  if (created) {
    return (
      <div className="mx-auto max-w-xl space-y-6">
        <h1 className="text-2xl font-bold">{created.key} created</h1>
        {created.token ? (
          <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-6 dark:border-emerald-900/50 dark:bg-emerald-950/40">
            <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
              Copy the token now — it is never shown again
            </p>
            <div className="flex gap-2">
              <Input value={created.token} readOnly className="font-mono" />
              <Button
                variant="secondary"
                size="icon"
                onClick={async () => {
                  await navigator.clipboard.writeText(created.token!);
                  toast.success("Copied");
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            This was a dry run, so no token exists. Move the createClient operation to live on
            the settings page to create clients for real.
          </p>
        )}
        <div className="flex gap-2">
          <Button onClick={() => navigate(`/dashboard/b2b/clients/${created.id}`)}>
            Open the client
          </Button>
          <Button variant="secondary" onClick={() => navigate("/dashboard/b2b/clients")}>
            Back to clients
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <Link
          to="/dashboard/b2b/clients"
          className="inline-flex items-center text-sm text-muted-foreground hover:underline"
        >
          <ArrowLeft className="mr-1 h-3.5 w-3.5" />
          All clients
        </Link>
        <h1 className="mt-2 text-2xl font-bold">New API client</h1>
      </div>

      <form
        className="space-y-5 rounded-xl border bg-card p-6"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <div className="space-y-1.5">
          <Label htmlFor="name">Customer name</Label>
          <Input
            id="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Acme Capital"
            required
          />
          <p className="text-xs text-muted-foreground">
            The client key becomes{" "}
            <code>TR_{name.trim().replace(/\s+/g, "_") || "<name>"}</code>, which the customer
            sends as the X-APIKey header.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="authType">Access type</Label>
          <select
            id="authType"
            className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            value={authType}
            onChange={(event) => setAuthType(event.target.value as AuthTypeName)}
          >
            {(["singleFeature", "full", "limited", "denied"] as AuthTypeName[]).map((type) => (
              <option key={type} value={type}>
                {AUTH_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="source">Start from</Label>
          <select
            id="source"
            className="h-9 w-full rounded-md border bg-background px-3 text-sm"
            value={source}
            onChange={(event) => setSource(event.target.value)}
          >
            <option value="">Nothing — grant endpoints afterwards</option>
            {(settings.data?.config.presets ?? []).map((preset) => (
              <option key={preset.id} value={`preset:${preset.id}`}>
                Preset — {preset.name}
              </option>
            ))}
            {(clients.data?.items ?? []).map((client) => (
              <option key={client.id} value={`client:${client.id}`}>
                Copy access from {client.key}
              </option>
            ))}
          </select>
          {source === "" && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              A client with no grants gets 401 on every endpoint until you grant some.
            </p>
          )}
        </div>

        <Button type="submit" disabled={create.isPending || name.trim() === ""}>
          {create.isPending ? "Creating…" : "Create client"}
        </Button>
      </form>
    </div>
  );
}
